import styled from '@emotion/styled';
import { useRef, useState, useEffect } from 'react';
import { mergeProps, useFocusRing, useTableCell } from 'react-aria';

import { SCOPES } from '../../../lib/scopes';
import { ToastManager } from '../../../ToastManager';

import { useApi } from '../../../store/SelectedHomeyStore.hooks';

import { su } from '../../../theme/functions/su';
import { HomeyColors } from '../../../theme/HomeyColors';
import { HomeyImages } from '../../../theme/HomeyImages';
import { theme } from '../../../theme/theme';

import { ContextMenu } from '../../../components/common/context-menu/ContextMenu';
import { ContextMenuButton } from '../../../components/common/context-menu/ContextMenuButton';
import { Glyph } from '../../../components/common/Glyph';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { Switch } from '../../../components/forms/Switch';

import { notificationSettingsStore } from './NotificationSettingsStore';
import { NotificationSettingsTableOwnersContextMenuContent } from './NotificationSettingsTableOwnersContextMenuContent';
import { useCellContext } from './SettingsTable';
import { SettingsTableRow } from './SettingsTableRow';

export function NotificationSettingsTableOwnersCell(props) {
  const { cell, row, state } = useCellContext();
  const [isPending, setIsPending] = useState(false);
  const [value, setValue] = useState(cell.props.value ?? false);

  const ref = useRef();
  const tableCell = useTableCell({ node: cell }, state, ref);
  const focusRing = useFocusRing();

  const { scopes } = useApi();

  useEffect(() => {
    setValue(cell.props.value ?? false);
  }, [cell.props.value]);

  function getContent() {
    switch (cell.column.key) {
      case 'category':
        return (
          <S.NameCell>
            <RoundIconWrapper size="24px" color={HomeyColors.getColorForUri(row.key)}>
              <Glyph
                size="18px"
                source={HomeyImages.getIconForUri(row.key)}
                color={theme.color.white}
              />
            </RoundIconWrapper>

            <S.Name>{cell.textValue}</S.Name>
            <S.Size>({row.value.size})</S.Size>
          </S.NameCell>
        );
      case 'timeline':
        return (
          <Switch
            name="timeline"
            aria-label="Timeline"
            value={value}
            isDisabled={isPending !== true && scopes[SCOPES.NOTIFICATIONS] !== true}
            onChange={(nextValue) => {
              const prevValue = value;
              setValue(nextValue);
              setIsPending(true);
              notificationSettingsStore
                .setOwnerEnabled({
                  uri: row.key,
                  enabled: nextValue,
                })
                .catch((error) => {
                  ToastManager.handleError(error);
                  setValue(prevValue);
                })
                .finally(() => {
                  setIsPending(false);
                });
            }}
          />
        );
      case 'push':
        return (
          <Switch
            name="push"
            aria-label="Push"
            value={row.value.timeline === false ? false : value}
            isDisabled={
              row.value.timeline === false ||
              (isPending !== true && scopes[SCOPES.NOTIFICATIONS] !== true)
            }
            onChange={(nextValue) => {
              const prevValue = value;
              setValue(nextValue);
              setIsPending(true);
              notificationSettingsStore
                .setOwnerPush({
                  uri: row.key,
                  push: nextValue,
                })
                .catch((error) => {
                  ToastManager.handleError(error);
                  setValue(prevValue);
                })
                .finally(() => {
                  setIsPending(false);
                });
            }}
          />
        );
      case 'actions':
        return (
          <S.ActionsCell>
            <ContextMenu
              content={
                <NotificationSettingsTableOwnersContextMenuContent
                  uri={row.key}
                  name={row.value.category}
                />
              }
            >
              {({ isOpen, onContextMenu }) => {
                return (
                  <ContextMenuButton
                    className={SettingsTableRow.contextMenuButtonClass}
                    isDisabled={scopes[SCOPES.NOTIFICATIONS] !== true}
                    isOpen={isOpen}
                    onPress={onContextMenu}
                  />
                );
              }}
            </ContextMenu>
          </S.ActionsCell>
        );
      default:
        break;
    }
  }

  return (
    <S.TableCell {...mergeProps(tableCell.gridCellProps, focusRing.focusProps)} ref={ref}>
      {getContent()}
    </S.TableCell>
  );
}

function S() {}
NotificationSettingsTableOwnersCell.S = S;

S.TableCell = styled.td`
  height: ${su(6)};
  outline: none;
  cursor: default;
  background-color: ${theme.color.component};
`;

S.ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

S.NameCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

S.Name = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-inline-start: ${su(1)};
`;

S.Size = styled.div`
  color: ${theme.color.text_light};
  padding-inline-start: 5px;
`;
